import React, {useState, useEffect} from "react";
import {Grid, FormControl, TextField, Typography} from "@mui/material";
import DriverInfoForm from "./DriverInfoForm";
import type {GroupedLocationInventoryActivity} from "../../../types/GroupedLocationInventoryActivity";
import {removeInitialSpecialChars} from "../../../util/sanitizeUtil";

interface AssetNumberCorrectionFormProps {
  inventory: GroupedLocationInventoryActivity;
  setCorrectAssetInfo: (correctAssetInfo: {
    containerNumber: string;
    chassisNumber: string;
    trailerNumber: string;
    truckLicensePlateNumber: string;
  }) => void;
  driverFieldsRequired?: boolean;
  driverFirst: string;
  setDriverFirst: (value: string) => void;
  driverLast: string;
  setDriverLast: (value: string) => void;
  driverLicense: string;
  setDriverLicense: (value: string) => void;
}

const AssetNumberCorrectionForm = ({
                                     inventory,
                                     setCorrectAssetInfo,
                                     driverFieldsRequired,
                                     driverFirst,
                                     setDriverFirst,
                                     driverLast,
                                     setDriverLast,
                                     driverLicense,
                                     setDriverLicense
                                   }: AssetNumberCorrectionFormProps) => {

  const {chassisNumber, truckLicensePlateNumber, containerNumber, trailerNumber} = inventory;

  const [correctChassisNumber, setCorrectChassisNumber] = useState(chassisNumber);
  const [correctContainerNumber, setCorrectContainerNumber] = useState(containerNumber);
  const [correctTrailerNumber, setCorrectTrailerNumber] = useState(trailerNumber);
  const [correctTruckLicensePlateNumber, setCorrectTruckLicensePlateNumber] = useState(truckLicensePlateNumber);

  const handleChangeTextField = ({target}: React.ChangeEvent<HTMLInputElement>) => {
    const value = removeInitialSpecialChars(target.value);
    switch (target.name) {
      case "trailerNumber":
        setCorrectTrailerNumber(value);
        break;
      case "containerNumber":
        setCorrectContainerNumber(value);
        break;
      case "chassisNumber":
        setCorrectChassisNumber(value);
        break;
      case "truckLicensePlateNumber":
        setCorrectTruckLicensePlateNumber(value);
        break;
      default:
        break;
    }
  }

  useEffect(() => {
    setCorrectAssetInfo({
      containerNumber: correctContainerNumber,
      chassisNumber: correctChassisNumber,
      trailerNumber: correctTrailerNumber,
      truckLicensePlateNumber: correctTruckLicensePlateNumber
    })
  }, [correctChassisNumber, correctContainerNumber, correctTrailerNumber, correctTruckLicensePlateNumber, setCorrectAssetInfo])

  return <Grid item container>
    <Grid item container component={'section'} gap={2} direction={"column"}>
      <Typography variant="h6">Asset Details</Typography>
      <Typography variant={"body1"} color={"textPrimary"}>Enter the correct equipment numbers.</Typography>
      <Typography variant={"body1"} color={"textPrimary"}>Add 'N/A' if there is no corresponding data.</Typography>
      <Grid item>
        <FormControl fullWidth>
          <TextField
            id="ss-select-correction-chassis-number"
            value={correctChassisNumber}
            type="text"
            name="chassisNumber"
            onChange={handleChangeTextField}
            label="Chassis Number"
          />
        </FormControl>
      </Grid>
      <Grid item>
        <FormControl fullWidth>
          <TextField
            id="ss-select-correction-container-number"
            value={correctContainerNumber}
            type="text"
            name="containerNumber"
            onChange={handleChangeTextField}
            label="Container Number"
          />
        </FormControl>
      </Grid>
      <Grid item>
        <FormControl fullWidth>
          <TextField
            id="ss-select-correction-trailer-number"
            value={correctTrailerNumber}
            type="text"
            name="trailerNumber"
            onChange={handleChangeTextField}
            label="Trailer Number"
          />
        </FormControl>
      </Grid>
      <Grid item>
        <FormControl fullWidth>
          <TextField
            id="ss-select-correction-truck-license-plate-number"
            value={correctTruckLicensePlateNumber}
            type="text"
            name="truckLicensePlateNumber"
            onChange={handleChangeTextField}
            label="Truck License Plate Number"
          />
        </FormControl>
      </Grid>
    </Grid>
    <DriverInfoForm
      driverFirstName={driverFirst}
      setDriverFirstName={setDriverFirst}
      driverLastName={driverLast}
      setDriverLastName={setDriverLast}
      driverLicenseNumber={driverLicense}
      setDriverLicenseNumber={setDriverLicense}
      required={driverFieldsRequired}
    />
  </Grid>

}

export default AssetNumberCorrectionForm;
